import { useState, useCallback, useEffect } from 'react';
import { parseISO, format, formatISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Modal from '../../components/Modal';
import { API } from '../../API';
import { Paginator } from '../../components/Paginator/Paginator';
import cn from 'classnames';
import * as d3 from 'd3';
import { ampli } from '../../ampli';
import { ExpandableText } from '../../components/CollapsibleText/ExpandableText';
import { ReactComponent as ArrowIcon } from '../NarrativesPage/assets/arrow.svg';
import { ReactComponent as LinkIcon } from '../../assets/link.svg';
import {ReactComponent as CalendarIcon} from '../../assets/calendar.svg';
import { ReactComponent as ViewsIcon } from '../../assets/views.svg';
import { ReactComponent as EngagementIcon } from '../../assets/engagement.svg';
import { ReactComponent as SimilarityIcon } from '../../assets/similarity.svg';
import { ReactComponent as FollowersIcon } from '../../assets/followers.svg';
import { ReactComponent as TranslationIcon } from '../../assets/translation.svg';
import { ReactComponent as OriginalTextIcon } from '../../assets/originalText.svg';
import { ReactComponent as ActorIcon } from '../../pages/SearchPage/assets/link.svg';
import { ReactComponent as SourceIcon } from '../../pages/SearchPage/assets/person.svg';
import { ReactComponent as SearchIcon } from '../../pages/SearchPage/assets/search.svg';
import { getSentimentName, getSentimentIcon } from "../../utils/sentiment";
import { ReactComponent as SettingsIcon } from '../../assets/settings.svg';
import { decodeSourceName } from '../../utils/decodeURI';
import {platformIconsMapping, platformNamesMapping} from '../../utils/platforms';
import {contentTypesIconsMapping, contentTypesMapping} from '../../utils/contentTypes';
import { LoaderSmall } from '../../components/LoaderSmall/LoaderSmall';
import { Loader } from '../../components/Loader/Loader';
import Tooltip from '../../components/Tooltip/Tooltip';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { replaceNewlinesWithBreaks } from '../../utils/replaceNewlinesWithBreaks';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { ButtonsList } from '../../components/DropdownMenu/ButtonsList';
import { formatNumber, formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { ImagesContainer } from '../../components/Image/ImagesContainer';
import { MessageInfoWrapper } from '../../components/MessagesTable/MessageInfoWrapper';
import { MessageMetricsWrapper } from '../../components/MessagesTable/MessageMetricsWrapper';
import { OriginalTextBlock } from '../../components/MessagesTable/OriginalTextBlock';
import { MessageTooltips } from '../../components/MessagesTable/MessageTooltips';

import messageStyles from "../../components/MessagesTable/ExtendedMessageView.module.scss";
import styles from '../../pages/NarrativePage/NarrativePage.module.scss';
import { showRequestError } from '../../utils/requestError';

export const MessagesModal = ({
  infoModal,
  sourceId,
  narrativeId,
  onChange,
  isShare=false,
}) => {
  const { t } = useTranslation();
  const [narrativeMessages, setNarrativeMessages] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser] = useCurrentUser();
  const [isMessageDropdownOpen, setIsMessageDropdownOpen] = useState({});

  const nativeLang = currentUser?.translation_language ? currentUser?.translation_language : window.clientInformation.language.split('-', 1)[0].toUpperCase();

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showSiilaritySearch = featureFlags?.includes('show_similarity_search');
  
  const [highlights, setHighlights] = useState(['KEYWORDS']);

  const fetchNarrativeMessages = useCallback((page=1) => {
    if (!narrativeId || !sourceId) {
      return;
    }

    setIsLoading(true);

    const urlParams = new URLSearchParams();
    urlParams.append('page', page);
    urlParams.append('size', 20);
    urlParams.append('narrative_id', narrativeId);
    urlParams.append('source_id', sourceId);

    API.fetch('GET', `/API/v1/messages?${urlParams.toString()}`).then(
      (data) => {
        setNarrativeMessages(data);
        setIsLoading(false);
      },
    ).catch((e) => {
      showRequestError(e, t('Narrative messages'));
      setIsLoading(false);
    });
    },
    [sourceId, narrativeId],
  );

  useEffect(() => {
    fetchNarrativeMessages()
  }, []);


  const translateMessage = (messageId, text, nativeLang, sourceLang='') => {
    let body;
    if(sourceLang) {
      body = {
        text: text,
        destination_language: nativeLang,
        source_language: sourceLang
      }
    } else {
      body = {
        text: text,
        destination_language: nativeLang,
      }
    }
    API.fetch('POST', '/API/v1/translations/translate', null, body).then((data) => {
      setNarrativeMessages({...narrativeMessages, objects: narrativeMessages.objects.map(message => {
      if(message.id === messageId) {
        message.isTranslation = true;
        message.translated = data.destination_text;
        return message;
      } else {
        return message;
      }
      })})
    })
    .catch(e => {
      setNarrativeMessages({...narrativeMessages, objects: narrativeMessages.objects.map(message => {
        if(message.id === messageId) {
          message.isTranslation = true;
          message.error = true;
          message.translated = t('We were not able to translate this text.');
          return message;
        } else {
          return message;
        }
        })})
    });
  }
  
  const toggleTranslate = (messageId, isTranslation) => {
    setNarrativeMessages({...narrativeMessages, objects: narrativeMessages.objects.map(message => {
      if(message.id === messageId) {
        message.isTranslation = isTranslation;
        return message;
      } else {
        return message;
      }
    })})
  }
  
  const handleTranslation = (messageId, text, isTranslation) => {
    const target = narrativeMessages.objects.find(message => message.id === messageId);
    if (target.translated) {
      toggleTranslate(messageId, isTranslation)
    } else {
      toggleTranslate(messageId, true)
      translateMessage(messageId, text, nativeLang)
    }
  }


  return (
    <Modal
      isVisible={infoModal}
      title={'Narrative messages'}
      content={
      <>
        {!narrativeMessages || isLoading ? <Loader/> : narrativeMessages.objects.length > 0 ? <div className="messages-modal-messages">
          <table className={messageStyles.extendedMessages}>
              <thead>
                <tr>
                  <td className={messageStyles.headerInfo}>
                    <span>{t('Total publications')}: {formatNumber(narrativeMessages.total)}</span>
                  </td>
                  <td className='message-controlls'>
                  </td>
                </tr>
              </thead>
              <tbody>
                {narrativeMessages.objects.map(message => {
                  let followers_cnt = message?.source?.audience;
                  let textClass = styles.extendedMessage;
                  let textClassParent = messageStyles.extendedMessage;

                  if (message?.parent_message?.error && message?.parent_message?.isTranslation) {
                    textClassParent = `${messageStyles.extendedMessage} ${messageStyles.error}`
                  } else if (message.error && !message.isTranslation) {
                    textClassParent = messageStyles.extendedMessage;
                  }
        
                  if(message.error && message.isTranslation) {
                    textClass = `${styles.extendedMessage} ${styles.error}`
                  } else if (message.error && !message.isTranslation) {
                    textClass = styles.extendedMessage;
                  }

                  let messageMenuOptions = []
                  if(showSiilaritySearch) {
                    messageMenuOptions.push({
                      name: t('Find similar publications'),
                      value: 'similar'
                    })
                  }

                  if((message?.content_type === 'COMMENT' && (message?.parent_message || message?.parent_id)) || message?.content_type === 'POST') {
                    messageMenuOptions.push({
                      name: t('Find comments to post'),
                      value: 'comments'
                    })
                  }

                  if (message?.content_type === 'VIDEO' && message?.source?.source_type === 'TIKTOK') {
                    messageMenuOptions.push({
                      name: t('Find comments to video'),
                      value: 'comments_to_video'
                    })
                  }

                  if (message?.content_type === 'POST' && ['TIKTOK', 'FACEBOOK', 'TELEGRAM'].includes(message?.source?.source_type)) {
                    messageMenuOptions.push({
                      name: t('Find reposts to post'),
                      value: 'reposts_to_post'
                    })
                  }

                  const textForTranslation = message.highlighted_text ? replaceNewlinesWithBreaks(message.highlighted_text) : replaceNewlinesWithBreaks(message.text);
        
                  return (
                    <tr key={message.id}>
                      <td>
                        <MessageInfoWrapper
                          message={message}
                          followers_cnt={followers_cnt}
                          isShare={isShare}
                          currentUser={currentUser}
                          narrative={null}
                          narrartiveMessage={null}
                          setMessageModal={null}
                        />

                        <div className={messageStyles.messageContainer}>
                          <MessageMetricsWrapper
                            message={message}
                            narrative={null}
                            isShare={isShare}
                            isMessageDropdownOpen={isMessageDropdownOpen}
                            setIsMessageDropdownOpen={setIsMessageDropdownOpen}
                            messageMenuOptions={messageMenuOptions}
                          />

                          {message.parent_message ? (
                            <OriginalTextBlock
                              message={message}
                              isShare={isShare}
                              currentUser={currentUser}
                              narrative={null}
                              textClassParent={textClassParent}
                            />
                          ) : (
                            ''
                          )}

                          {message?.images && message?.images?.length > 0 ? (
                            <ImagesContainer
                              images={message?.images}
                              showCount={3}
                              className={styles.imagesContainer}
                            />
                          ) : (
                            ''
                          )}

                          <div className={messageStyles.textBlock}>
                            {message.isTranslation === true &&
                            !message.translated ? (
                              <div className={messageStyles.loaderContainer}>
                                <LoaderSmall />
                              </div>
                            ) : (
                              <ExpandableText
                                length={150}
                                highlights={highlights}
                                text={
                                  message.isTranslation
                                    ? message.translated
                                    : message.highlighted_text || message.text
                                }
                                textClassName={textClass}
                              />
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="message-controlls">
                        <div className={messageStyles.translationWrapper}>
                          <span
                            className={
                              message.isTranslation ? '' : messageStyles.active
                            }
                            onClick={() =>
                              handleTranslation(
                                message.id,
                                textForTranslation,
                                false,
                              )
                            }
                          >
                            <OriginalTextIcon />
                          </span>
                          <span
                            className={
                              message.isTranslation ? messageStyles.active : ''
                            }
                            onClick={() =>
                              handleTranslation(
                                message.id,
                                textForTranslation,
                                true,
                              )
                            }
                          >
                            <TranslationIcon />
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <MessageTooltips scope=".messages-modal-messages" />
          </div> : <div className={styles.noData}>{t('There is no data to display')}</div>}
        </>
      }
      footer={
        <>
          <Paginator
            size={20}
            page={page}
            total={narrativeMessages?.total}
            onPageChange={(newPage) =>{
              setPage(newPage)
              fetchNarrativeMessages(newPage)
            }}
          />
        </>
      }
      onClose={() => {
        onChange(false);
      }}
      className={styles.chartTreeModal}
    />
  );
};
